import { useCallback, useMemo, useState } from 'react';
import {
    Button,
    Grid,
    GridItem,
    DropdownItem,
    Checkbox,
    ModalVariant,
} from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash-es';
import { CodingScheme, GlobalContouringRoi, ContouringRoi, isContouringRoi } from '../../../store/contouring/contouring-types';
import ConfirmationModal from '../../../components/confirmation-modal';
import { roiCodingSchemeCopied, roiCodingSchemeCopiedToOutput, roiCodingSchemeUpdated } from '../../../store/contouring/contouringSlice';
import SplitButton from '../../../components/split-button';
import DebouncedTextInput from '../../../components/debounced-text-input';
import FormGroupTruncated from '../../../components/form-group-truncated';
import HelpPopover from '../../../components/help-popover';
import { appConfigSelectors } from '../../../store/appConfig/appConfigSlice';
import { columnIds, columnTooltips } from './ContourRoiTable';

import './coding-scheme-fields.css';

const GRID_SPAN = 4;
const CODING_SCHEME_DEBOUNCE_TIME_MS = 600;
const DEFAULT_SKIP_NON_EMPTY = true;

const enum CodingSchemeCopyTarget {
    AllRois = 'all-rois',
    WithinOutput = 'within-output',
}

enum CopyableCodingSchemeField {
    Designator = 'codingSchemeDesignator',
    Version = 'codingSchemeVersion',
    MappingResource = 'codingSchemeMappingResource',
    ContextGroupVersion = 'codingSchemeContextGroupVersion',
    ContextIdentifier = 'codingSchemeContextIdentifier',
    ContextUid = 'codingSchemeContextUid',
    MappingResourceUid = 'codingSchemeMappingResourceUid',
    MappingResourceName = 'codingSchemeMappingResourceName',
}

const ALL_COPYABLE_FIELDS = Object.values(CopyableCodingSchemeField);

interface CodingSchemeFieldsProps {
    roi: GlobalContouringRoi | ContouringRoi;
    isGlobalRoi: boolean;
}

interface ClickableCheckboxProps {
    field: CopyableCodingSchemeField;
    label: string;
    isChecked: boolean;
    onChange: (field: CopyableCodingSchemeField, checked: boolean) => void;
}

/** Generates a checkbox with some extra room around it for easier clicking. When copy mode is
 * active, clicking the field label will also toggle this checkbox via the fieldId linkage.
 */
const ClickableCheckboxWithExtraRoom = ({ field, label, isChecked, onChange }: ClickableCheckboxProps) => (
    <div className="coding-scheme-copy-checkbox">
        <label className="coding-scheme-copy-checkbox-fake-label">
            <Checkbox
                id={`coding-scheme-checkbox-${field}`}
                isChecked={isChecked}
                onChange={(_event, checked) => onChange(field, checked)}
                aria-label={`Select ${label} for copying to other structures`}
                data-testid={`coding-scheme-checkbox-${field}`}
            />
        </label>
    </div>
);

const CodingSchemeFields = ({ roi, isGlobalRoi }: CodingSchemeFieldsProps) => {
    const [isCopyFmaIdsConfirmationModalOpen, setCopyFmaIdsConfirmationModalOpen] = useState(false);
    const [confirmationModalKey, setConfirmationModalKey] = useState(CodingSchemeCopyTarget.AllRois);
    const [isCopyModeActive, setIsCopyModeActive] = useState(false);
    const [selectedFieldsForCopy, setSelectedFieldsForCopy] = useState<Set<CopyableCodingSchemeField>>(new Set(ALL_COPYABLE_FIELDS));
    const [skipNonEmpty, setSkipNonEmpty] = useState(DEFAULT_SKIP_NON_EMPTY);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);

    const codingSchemeChangeHandlers = useMemo(() => {
        const createHandler = (fieldKey: string) => (text: string) => {
            const currentValue = get(roi, fieldKey, undefined) || '';
            if (text !== currentValue) {
                dispatch(roiCodingSchemeUpdated({ roiId: roi.id, codingScheme: { [fieldKey]: text }, isGlobalRoi }));
            }
        };
        return {
            codingSchemeCodeValue: createHandler('codingSchemeCodeValue'),
            codingSchemeCodeMeaning: createHandler('codingSchemeCodeMeaning'),
            codingSchemeDesignator: createHandler('codingSchemeDesignator'),
            codingSchemeVersion: createHandler('codingSchemeVersion'),
            codingSchemeMappingResource: createHandler('codingSchemeMappingResource'),
            codingSchemeContextGroupVersion: createHandler('codingSchemeContextGroupVersion'),
            codingSchemeContextIdentifier: createHandler('codingSchemeContextIdentifier'),
            codingSchemeContextUid: createHandler('codingSchemeContextUid'),
            codingSchemeMappingResourceUid: createHandler('codingSchemeMappingResourceUid'),
            codingSchemeMappingResourceName: createHandler('codingSchemeMappingResourceName'),
        };
    }, [dispatch, isGlobalRoi, roi]);

    const handleToggleCopyMode = useCallback(() => {
        setIsCopyModeActive(prev => {
            if (prev) {
                setSelectedFieldsForCopy(new Set(ALL_COPYABLE_FIELDS));
                setSkipNonEmpty(DEFAULT_SKIP_NON_EMPTY);
            }
            return !prev;
        });
    }, []);

    const handleFieldSelectionChange = useCallback((fieldKey: CopyableCodingSchemeField, isChecked: boolean) => {
        setSelectedFieldsForCopy(prev => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(fieldKey);
            } else {
                newSet.delete(fieldKey);
            }
            return newSet;
        });
    }, []);

    const extractSelectedCodingSchemeAttributes = useCallback((roiCustomization: GlobalContouringRoi | ContouringRoi): Partial<CodingScheme> => {
        const extracted: Partial<CodingScheme> = {};

        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.Designator)) {
            extracted.codingSchemeDesignator = roiCustomization.codingSchemeDesignator;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.Version)) {
            extracted.codingSchemeVersion = roiCustomization.codingSchemeVersion;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.MappingResource)) {
            extracted.codingSchemeMappingResource = roiCustomization.codingSchemeMappingResource;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.ContextGroupVersion)) {
            extracted.codingSchemeContextGroupVersion = roiCustomization.codingSchemeContextGroupVersion;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.ContextIdentifier)) {
            extracted.codingSchemeContextIdentifier = roiCustomization.codingSchemeContextIdentifier;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.ContextUid)) {
            extracted.codingSchemeContextUid = roiCustomization.codingSchemeContextUid;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.MappingResourceUid)) {
            extracted.codingSchemeMappingResourceUid = roiCustomization.codingSchemeMappingResourceUid;
        }
        if (selectedFieldsForCopy.has(CopyableCodingSchemeField.MappingResourceName)) {
            extracted.codingSchemeMappingResourceName = roiCustomization.codingSchemeMappingResourceName;
        }

        return extracted;
    }, [selectedFieldsForCopy]);

    const handleCopyCodingSchemeToAllCustomizations = useCallback((codingScheme: Partial<CodingScheme>, skipNonEmptyFields: boolean) => {
        dispatch(roiCodingSchemeCopied({ codingSchemeChanges: codingScheme, skipNonEmpty: skipNonEmptyFields }));
        setIsCopyModeActive(false);
        setSelectedFieldsForCopy(new Set(ALL_COPYABLE_FIELDS));
        setSkipNonEmpty(DEFAULT_SKIP_NON_EMPTY);
    }, [dispatch]);

    const handleCopyCodingSchemeToCurrentOutput = useCallback((codingScheme: Partial<CodingScheme>, skipNonEmptyFields: boolean) => {
        if (isContouringRoi(roi)) {
            dispatch(roiCodingSchemeCopiedToOutput({ codingSchemeChanges: codingScheme, outputId: roi.customizationOutputId, skipNonEmpty: skipNonEmptyFields }));
            setIsCopyModeActive(false);
            setSelectedFieldsForCopy(new Set(ALL_COPYABLE_FIELDS));
            setSkipNonEmpty(DEFAULT_SKIP_NON_EMPTY);
        }
    }, [dispatch, roi]);

    const onCopyFmaCodesClick = useCallback((type: CodingSchemeCopyTarget) => {
        if (type === CodingSchemeCopyTarget.AllRois) {
            handleCopyCodingSchemeToAllCustomizations(extractSelectedCodingSchemeAttributes(roi), skipNonEmpty);
        } else if (type === CodingSchemeCopyTarget.WithinOutput) {
            handleCopyCodingSchemeToCurrentOutput(extractSelectedCodingSchemeAttributes(roi), skipNonEmpty);
        }
    }, [roi, handleCopyCodingSchemeToAllCustomizations, handleCopyCodingSchemeToCurrentOutput, extractSelectedCodingSchemeAttributes, skipNonEmpty]);

    const handleOpenCopyFmaIdsConfirmationModal = useCallback((key: CodingSchemeCopyTarget) => {
        setConfirmationModalKey(key);
        setCopyFmaIdsConfirmationModalOpen(true);
    }, []);

    const handleCopyAllROIsFmaIdsClick = useCallback(() => {
        handleOpenCopyFmaIdsConfirmationModal(CodingSchemeCopyTarget.AllRois);
    }, [handleOpenCopyFmaIdsConfirmationModal]);

    const handleCopyROIsWithinOutputFmaIdsClick = useCallback(() => {
        handleOpenCopyFmaIdsConfirmationModal(CodingSchemeCopyTarget.WithinOutput);
    }, [handleOpenCopyFmaIdsConfirmationModal]);

    const handleSkipNonEmptyChange = useCallback((_event: React.FormEvent<HTMLInputElement>, checked: boolean) => {
        setSkipNonEmpty(checked);
    }, []);

    const copyFmaIdsDropdownItems = useMemo(() => [
        <DropdownItem
            key={CodingSchemeCopyTarget.AllRois}
            onClick={handleCopyAllROIsFmaIdsClick}>
            <div data-testid="copy-fma-ids-split-button-menu-all-rois">{t('fullRoiDetails.copyFmaIds.allRois')}</div>
        </DropdownItem>,
        <DropdownItem
            key={CodingSchemeCopyTarget.WithinOutput}
            onClick={handleCopyROIsWithinOutputFmaIdsClick}
        >
            <div data-testid="copy-fma-ids-split-button-menu-within-output">{t('fullRoiDetails.copyFmaIds.withinOutput')}</div>
        </DropdownItem>
    ], [handleCopyAllROIsFmaIdsClick, handleCopyROIsWithinOutputFmaIdsClick, t]);

    return (
        <>
            {isCopyFmaIdsConfirmationModalOpen && (
                <ConfirmationModal
                    isOpen={isCopyFmaIdsConfirmationModalOpen}
                    title={confirmationModalKey === CodingSchemeCopyTarget.AllRois ? t('fullRoiDetails.copyFmaIdsInUserConfigConfirm.title') : t('fullRoiDetails.copyFmaIdsInOutputConfigConfirm.title')}
                    description={confirmationModalKey === CodingSchemeCopyTarget.AllRois ? t('fullRoiDetails.copyFmaIdsInUserConfigConfirm.message') : t('fullRoiDetails.copyFmaIdsInOutputConfigConfirm.message')}
                    onConfirm={() => {
                        onCopyFmaCodesClick(confirmationModalKey);
                        setCopyFmaIdsConfirmationModalOpen(false);
                    }}
                    onClose={() => setCopyFmaIdsConfirmationModalOpen(false)}
                    variant={ModalVariant.small}
                    testIdPrefix='copy-coding-scheme'
                />
            )}

            <Grid className='fma-grid-gutter'>
                <GridItem span={8} className='fma-copy-non-highlight'>
                    <GridItem span={GRID_SPAN}>
                        <FormGroupTruncated
                            fieldId="codingSchemeCodeValue"
                            label={t('fullRoiDetails.codingScheme.codeValue')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeCodeValue"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeCodeValue}
                                onBlur={codingSchemeChangeHandlers.codingSchemeCodeValue}
                                defaultValue={roi.codingSchemeCodeValue || ''}
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN}>
                        <FormGroupTruncated
                            fieldId="codingSchemeCodeMeaning"
                            label={t('fullRoiDetails.codingScheme.codeMeaning')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeCodeMeaning"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeCodeMeaning}
                                onBlur={codingSchemeChangeHandlers.codingSchemeCodeMeaning}
                                defaultValue={roi.codingSchemeCodeMeaning || ''}
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                </GridItem>
                <Grid className='fma-copy-highlight fma-grid-gutter'>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.Designator}
                                label={t('fullRoiDetails.codingScheme.codeSchemeDesignator')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.Designator)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.Designator}` : 'codingSchemeDesignator'}
                            label={t('fullRoiDetails.codingScheme.codeSchemeDesignator')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeDesignator"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeDesignator}
                                onBlur={codingSchemeChangeHandlers.codingSchemeDesignator}
                                defaultValue={roi.codingSchemeDesignator || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.Version}
                                label={t('fullRoiDetails.codingScheme.codeSchemeVersion')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.Version)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.Version}` : 'codingSchemeVersion'}
                            label={t('fullRoiDetails.codingScheme.codeSchemeVersion')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeVersion"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeVersion}
                                onBlur={codingSchemeChangeHandlers.codingSchemeVersion}
                                defaultValue={roi.codingSchemeVersion || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.MappingResource}
                                label={t('fullRoiDetails.codingScheme.mappingResource')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.MappingResource)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.MappingResource}` : 'codingSchemeMappingResource'}
                            label={t('fullRoiDetails.codingScheme.mappingResource')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeMappingResource"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeMappingResource}
                                onBlur={codingSchemeChangeHandlers.codingSchemeMappingResource}
                                defaultValue={roi.codingSchemeMappingResource || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.ContextGroupVersion}
                                label={t('fullRoiDetails.codingScheme.contextGroupVersion')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.ContextGroupVersion)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.ContextGroupVersion}` : 'codingSchemeContextGroupVersion'}
                            label={t('fullRoiDetails.codingScheme.contextGroupVersion')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeContextGroupVersion"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeContextGroupVersion}
                                onBlur={codingSchemeChangeHandlers.codingSchemeContextGroupVersion}
                                defaultValue={roi.codingSchemeContextGroupVersion || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.ContextIdentifier}
                                label={t('fullRoiDetails.codingScheme.contextIdentifier')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.ContextIdentifier)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.ContextIdentifier}` : 'codingSchemeContextIdentifier'}
                            label={t('fullRoiDetails.codingScheme.contextIdentifier')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeContextIdentifier"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeContextIdentifier}
                                onBlur={codingSchemeChangeHandlers.codingSchemeContextIdentifier}
                                defaultValue={roi.codingSchemeContextIdentifier || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.ContextUid}
                                label={t('fullRoiDetails.codingScheme.contextUid')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.ContextUid)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.ContextUid}` : 'codingSchemeContextUid'}
                            label={t('fullRoiDetails.codingScheme.contextUid')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeContextUid"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeContextUid}
                                onBlur={codingSchemeChangeHandlers.codingSchemeContextUid}
                                defaultValue={roi.codingSchemeContextUid || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.MappingResourceUid}
                                label={t('fullRoiDetails.codingScheme.mappingResourceUid')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.MappingResourceUid)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.MappingResourceUid}` : 'codingSchemeMappingResourceUid'}
                            label={t('fullRoiDetails.codingScheme.mappingResourceUid')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeMappingResourceUid"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeMappingResourceUid}
                                onBlur={codingSchemeChangeHandlers.codingSchemeMappingResourceUid}
                                defaultValue={roi.codingSchemeMappingResourceUid || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={GRID_SPAN} className={isCopyModeActive ? 'copyable-field-with-checkbox' : ''}>
                        {isCopyModeActive && (
                            <ClickableCheckboxWithExtraRoom
                                field={CopyableCodingSchemeField.MappingResourceName}
                                label={t('fullRoiDetails.codingScheme.mappingResourceName')}
                                isChecked={selectedFieldsForCopy.has(CopyableCodingSchemeField.MappingResourceName)}
                                onChange={handleFieldSelectionChange}
                            />
                        )}
                        <FormGroupTruncated
                            fieldId={isCopyModeActive ? `coding-scheme-checkbox-${CopyableCodingSchemeField.MappingResourceName}` : 'codingSchemeMappingResourceName'}
                            label={t('fullRoiDetails.codingScheme.mappingResourceName')}>
                            <DebouncedTextInput
                                fieldId="codingSchemeMappingResourceName"
                                onDebouncedChange={codingSchemeChangeHandlers.codingSchemeMappingResourceName}
                                onBlur={codingSchemeChangeHandlers.codingSchemeMappingResourceName}
                                defaultValue={roi.codingSchemeMappingResourceName || ''}
                                className='copyable-input'
                                debounceTimeMs={CODING_SCHEME_DEBOUNCE_TIME_MS}
                            />
                        </FormGroupTruncated>
                    </GridItem>
                    <GridItem span={12}>
                        <div className="coding-scheme-copy-buttons">
                            {!isCopyModeActive ? (
                                <Button
                                    onClick={handleToggleCopyMode}
                                    variant="secondary"
                                    data-testid="copy-coding-schemes-button"
                                >
                                    {t('fullRoiDetails.copyFmaIds.startCopy')}
                                </Button>
                            ) : (
                                <>
                                    {isGlobalRoi ? (
                                        <Button
                                            onClick={() => handleOpenCopyFmaIdsConfirmationModal(CodingSchemeCopyTarget.AllRois)}
                                            variant="primary"
                                            isDisabled={selectedFieldsForCopy.size === 0}
                                        >
                                            {t('fullRoiDetails.copyFmaIds.allRois')}
                                        </Button>
                                    ) : (
                                        <SplitButton
                                            primaryLabel={t('fullRoiDetails.copyFmaIds.allRois')}
                                            dropdownItems={copyFmaIdsDropdownItems}
                                            onPrimaryAction={handleCopyAllROIsFmaIdsClick}
                                            id="copy-fma-ids"
                                            isDisabled={selectedFieldsForCopy.size === 0}
                                        />
                                    )}
                                    <Button
                                        onClick={handleToggleCopyMode}
                                        variant="link"
                                        data-testid="cancel-copy-coding-scheme-button"
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                    <Checkbox
                                        id="skip-non-empty-checkbox"
                                        label={<span>{t('fullRoiDetails.copyFmaIds.skipNonEmpty')}</span>}
                                        isChecked={skipNonEmpty}
                                        onChange={handleSkipNonEmptyChange}
                                        className="coding-scheme-skip-non-empty-checkbox"
                                        data-testid="skip-non-empty-checkbox"
                                    />
                                    <HelpPopover
                                        headerContent={t('fullRoiDetails.copyFmaIds.skipNonEmpty')}
                                        bodyContent={t('fullRoiDetails.copyFmaIds.skipNonEmpty.tooltip')}
                                        className="coding-scheme-skip-non-empty-help-popover"
                                    />
                                </>
                            )}
                        </div>
                        {isCopyModeActive && (
                            <div className="coding-scheme-copy-instructions" data-testid="coding-scheme-copy-instructions">
                                {t('fullRoiDetails.copyFmaIds.instructions')}
                            </div>
                        )}
                    </GridItem>
                </Grid>
                <GridItem span={12}>
                    <div className="coding-scheme-default-values-label">
                        {columnTooltips[columnIds.fmaCodeValue](t, deploymentInfo?.defaultRoiCodesUrl)}
                    </div>
                </GridItem>
            </Grid>
        </>
    );
};

export default CodingSchemeFields;
